import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  LinearProgress,
  Alert,
  Skeleton,
  Breadcrumbs,
  Link,
  Paper,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  PlayArrow,
  Pause,
  VolumeUp,
  Fullscreen,
  CheckCircle,
  Assignment,
  NavigateNext,
  NavigateBefore,
  Flag,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import LessonEvaluationDialog from '../components/LessonEvaluationDialog';

interface Lesson {
  id: string;
  title: string;
  content: string;
  contentType: 'text' | 'video' | 'interactive' | 'document';
  mediaUrl?: string;
  duration: number;
  order: number;
  objectives: string[];
  evaluationId?: string;
}

interface Course {
  id: string;
  title: string;
  lessons: Lesson[];
}

const LessonView: React.FC = () => {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [currentLesson, setCurrentLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [evaluationResult, setEvaluationResult] = useState<any>(null);
  const [showBlockedMessage, setShowBlockedMessage] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [lessonCompleted, setLessonCompleted] = useState(false);
  const [showCourseCompleted, setShowCourseCompleted] = useState(false);
  
  // Debug del estado showEvaluation
  useEffect(() => {
    console.log('showEvaluation cambió a:', showEvaluation);
  }, [showEvaluation]);

  // Debug del estado evaluationResult
  useEffect(() => {
    console.log('evaluationResult cambió a:', evaluationResult);
  }, [evaluationResult]);

  // Debug del estado lessonCompleted
  useEffect(() => {
    console.log('lessonCompleted cambió a:', lessonCompleted);
  }, [lessonCompleted]);

  useEffect(() => {
    fetchCourseAndLesson();
  }, [courseId, lessonId]);

  // Cargar script de Vimeo API
  useEffect(() => {
    const loadVimeoScript = () => {
      // Verificar si el script ya existe
      if (document.querySelector('script[src="https://player.vimeo.com/api/player.js"]')) {
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://player.vimeo.com/api/player.js';
      script.async = true;
      document.head.appendChild(script);
    };

    loadVimeoScript();
  }, []);

  const fetchCourseAndLesson = async () => {
    console.log('🚀 fetchCourseAndLesson iniciado');
    try {
      setLoading(true);
      const response: any = await api.getCourse(courseId || '');
      
      // Acceder directamente a response (que es el curso)
      const courseData = {
        id: response._id || response.id || courseId || '',
        title: response.title,
        lessons: (response.lessons || []).map((lesson: any) => ({
          id: lesson._id ? lesson._id.toString() : lesson.id, // Convertir _id a string si existe
          title: lesson.title,
          content: lesson.content,
          contentType: lesson.contentType,
          mediaUrl: lesson.mediaUrl,
          duration: lesson.duration,
          order: lesson.order,
          objectives: lesson.objectives || [],
          evaluationId: lesson.evaluationId
        })),
      };
      
      console.log('Looking for lessonId:', lessonId);
      console.log('Available lessons:', courseData.lessons.map((l: any) => ({ id: l.id, title: l.title })));
      
      setCourse(courseData);

      // Encontrar la lección actual
      const lesson = courseData.lessons.find((l: any) => l.id === lessonId);
      
      if (lesson) {
        setCurrentLesson(lesson);
        
        // Verificar si la evaluación ya fue aprobada para esta lección
        if (lesson.evaluationId) {
          console.log('🔍 Lección tiene evaluación, verificando progreso...');
          await checkEvaluationProgress(lesson.evaluationId);
        } else {
          console.log('🔍 Lección no tiene evaluación asociada');
        }
        
        // Verificar si la lección ya fue completada
        console.log('🔍 Verificando progreso de lección...');
        await checkLessonProgress(lesson.id);
      } else {
        setError('Lección no encontrada');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error cargando la lección');
    } finally {
      setLoading(false);
      console.log('✅ fetchCourseAndLesson completado');
    }
  };

  const checkEvaluationProgress = async (evaluationId: string) => {
    try {
      console.log('🔍 Verificando progreso de evaluación:', evaluationId);
      
      const progressResponse = await api.getCourseProgress(courseId || '');
      const evaluationAttempts = progressResponse.evaluationAttempts || [];
      const requiredId = String(
        (evaluationId as any)?._id || evaluationId || ''
      );

      const passedAttempt = evaluationAttempts.find((attempt: any) => {
        const attemptEvaluationId = String(
          attempt.evaluation?._id || attempt.evaluation || ''
        );
        return attemptEvaluationId === requiredId && attempt.passed;
      });
      
      if (passedAttempt) {
        console.log('✅ Evaluación ya aprobada anteriormente:', passedAttempt);
        
        const mockResult = {
          score: passedAttempt.score,
          earnedPoints: passedAttempt.earnedPoints,
          totalPoints: passedAttempt.totalPoints,
          passed: true,
          results: passedAttempt.results || []
        };
        
        setEvaluationResult(mockResult);
      } else {
        setEvaluationResult(null);
        console.log('❌ No se encontró evaluación aprobada para esta lección');
      }
    } catch (error) {
      console.log('⚠️ Error verificando progreso de evaluación:', error);
    }
  };

  const checkLessonProgress = async (lessonId: string) => {
    try {
      console.log('🔍 Verificando progreso de lección:', lessonId);
      
      // Obtener el progreso del curso para ver si la lección ya fue completada
      const progressResponse = await api.getCourseProgress(courseId || '');
      
      // Buscar la lección en lessonsProgress
      const lessonProgress = progressResponse.lessonsProgress?.find((lp: any) => 
        lp.lesson.toString() === lessonId && lp.status === 'completed'
      );
      
      if (lessonProgress) {
        console.log('✅ Lección ya completada anteriormente:', lessonProgress);
        setLessonCompleted(true);
      } else {
        console.log('❌ Lección no completada aún');
        setLessonCompleted(false);
      }
    } catch (error) {
      console.log('⚠️ Error verificando progreso de lección:', error);
    }
  };

  const handleBackToCourse = () => {
    navigate(`/courses/${courseId}`);
  };

  const handleNextLesson = () => {
    if (!course || !currentLesson) {
      console.log('❌ No se puede navegar: course o currentLesson no disponibles');
      return;
    }
    
    console.log('🔍 Buscando siguiente lección...');
    console.log('🔍 lessonId actual:', lessonId);
    console.log('🔍 currentLesson.id:', currentLesson.id);
    console.log('🔍 Lecciones disponibles:', course.lessons.map(l => ({ id: l.id, title: l.title })));
    
    const currentIndex = course.lessons.findIndex(l => l.id === currentLesson.id);
    console.log('🔍 Índice actual:', currentIndex);
    
    if (currentIndex < course.lessons.length - 1) {
      const nextLesson = course.lessons[currentIndex + 1];
      console.log('🔍 Siguiente lección:', nextLesson);
      console.log('🔍 Navegando a:', `/courses/${courseId}/lessons/${nextLesson.id}`);
      navigate(`/courses/${courseId}/lessons/${nextLesson.id}`);
    } else {
      const hasEval = !!currentLesson.evaluationId;
      const evalApproved = !hasEval || evaluationResult?.passed;
      if (lessonCompleted && evalApproved) {
        console.log('🎉 ¡Última lección completada! Mostrando mensaje de éxito');
        setShowCourseCompleted(true);
      } else {
        console.log('⚠️ Evaluación pendiente o lección sin completar');
        setShowBlockedMessage(true);
      }
    }
  };

  const handleFinishCourse = () => {
    console.log('🏁 Finalizando curso, navegando a la página del curso');
    navigate(`/courses/${courseId}`);
  };

  const handlePreviousLesson = () => {
    if (!course || !currentLesson) return;
    
    const currentIndex = course.lessons.findIndex(l => l.id === currentLesson.id);
    
    if (currentIndex > 0) {
      const prevLesson = course.lessons[currentIndex - 1];
      navigate(`/courses/${courseId}/lessons/${prevLesson.id}`);
    }
  };

  const handleCompleteLesson = async () => {
    try {
      if (currentLesson?.evaluationId && !evaluationResult?.passed) {
        setError('Debe aprobar la evaluación de esta lección con la nota mínima requerida antes de marcarla como completada.');
        return false;
      }

      console.log('🔍 Verificando matriculación...');
      
      // Marcar lección como completada
      await api.completeLesson(courseId || '', lessonId || '', currentLesson?.duration || 0);
      setLessonCompleted(true);
      
      // NO navegar automáticamente, dejar que el usuario decida
      console.log('✅ Lección completada exitosamente');
      return true; // Indicar éxito
    } catch (err: any) {
      console.error('❌ Error completando lección:', err.response?.data);
      
      if (err.response?.status === 404 && err.response?.data?.message === 'No está matriculado en este curso') {
        // El usuario no está matriculado, intentar matricularlo automáticamente
        console.log('🔄 Usuario no matriculado, intentando matricular automáticamente...');
        try {
          await api.enrollInCourse(courseId || '');
          console.log('✅ Usuario matriculado exitosamente');
          
          // Intentar completar la lección nuevamente
          await api.completeLesson(courseId || '', lessonId || '', currentLesson?.duration || 0);
          setLessonCompleted(true);
          console.log('✅ Lección completada exitosamente después de matricular');
          return true;
        } catch (enrollError: any) {
          console.error('❌ Error matriculando usuario:', enrollError);
          setError('Error al matricular en el curso. Intente de nuevo.');
          return false;
        }
      } else {
        setError(err.response?.data?.message || 'Error completando la lección');
        return false; // Indicar error
      }
    }
  };

  const handleStartEvaluation = () => {
    console.log('Botón "Tomar Evaluación" clickeado');
    console.log('evaluationId:', currentLesson?.evaluationId);
    console.log('showEvaluation antes:', showEvaluation);
    console.log('Renderizando diálogo de evaluación con evaluationId:', currentLesson?.evaluationId);
    
    // Abrir directamente el diálogo de evaluación
    setShowEvaluation(true);
    
    console.log('setShowEvaluation(true) ejecutado');
  };

  const handleEvaluationComplete = (result: any) => {
    console.log('✅ Evaluación completada:', result);
    
    // Solo cerrar el diálogo de evaluación
    setShowEvaluation(false);
    
    // Mostrar resultado en un popup/diálogo
    setEvaluationResult(result);
    
    // NO completar automáticamente la lección, dejar que el usuario decida
    console.log('📋 Mostrando resultado de evaluación, esperando decisión del usuario');
  };

  const handleBlockedNext = () => {
    setShowBlockedMessage(true);
  };

  const renderLessonContent = () => {
    if (!currentLesson) return null;

    switch (currentLesson.contentType) {
      case 'video':
        const getEmbedUrl = (url: string) => {
          // Convertir URL de Vimeo a formato embed
          if (url.includes('vimeo.com/')) {
            const videoId = url.split('/').pop();
            // Usar el formato exacto de Vimeo con todos los parámetros necesarios
            return `https://player.vimeo.com/video/${videoId}?badge=0&autopause=0&player_id=0&app_id=58479`;
          }
          // Para YouTube
          if (url.includes('youtube.com/watch?v=')) {
            const videoId = url.split('v=')[1]?.split('&')[0];
            return `https://www.youtube.com/embed/${videoId}`;
          }
          if (url.includes('youtu.be/')) {
            const videoId = url.split('/').pop()?.split('?')[0];
            return `https://www.youtube.com/embed/${videoId}`;
          }
          // Para otros videos, devolver la URL original
          return url;
        };

        const embedUrl = getEmbedUrl(currentLesson.mediaUrl || '');
        
        // Si es un video embebido (Vimeo, YouTube), usar iframe
        if (embedUrl.includes('player.vimeo.com') || embedUrl.includes('youtube.com/embed')) {
          return (
            <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
              <Box
                sx={{
                  position: 'relative',
                  paddingBottom: '56.25%', // 16:9 aspect ratio
                  height: 0,
                  overflow: 'hidden',
                  borderRadius: 2,
                  boxShadow: 2
                }}
              >
                <iframe
                  src={embedUrl}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  title={currentLesson.title}
                />
              </Box>
            </Box>
          );
        } else {
          // Para videos directos (MP4, etc.)
          return (
            <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
              <video
                controls
                style={{ width: '100%', borderRadius: 8 }}
                src={currentLesson.mediaUrl}
              >
                Tu navegador no soporta el elemento de video.
              </video>
            </Box>
          );
        }
      
      case 'document':
        return (
          <Box sx={{ width: '100%', height: 600 }}>
            <iframe
              src={currentLesson.mediaUrl}
              style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
              title={currentLesson.title}
            />
          </Box>
        );
      
      case 'interactive':
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Actividad Interactiva
              </Typography>
              <Typography variant="body1" paragraph>
                {currentLesson.content}
              </Typography>
              {currentLesson.mediaUrl && (
                <Button
                  variant="contained"
                  href={currentLesson.mediaUrl}
                  target="_blank"
                  startIcon={<PlayArrow />}
                >
                  Abrir Herramienta Interactiva
                </Button>
              )}
            </CardContent>
          </Card>
        );
      
      case 'text':
      default:
        return (
          <Card>
            <CardContent>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                {currentLesson.content}
              </Typography>
            </CardContent>
          </Card>
        );
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="60%" height={40} />
        <Skeleton variant="rectangular" height={400} sx={{ mt: 2 }} />
        <Skeleton variant="text" width="80%" sx={{ mt: 2 }} />
        <Skeleton variant="text" width="60%" />
      </Box>
    );
  }

  if (error || !course || !currentLesson) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Lección no encontrada'}</Alert>
        <Button onClick={handleBackToCourse} sx={{ mt: 2 }}>
          Volver al curso
        </Button>
      </Box>
    );
  }

  const currentIndex = course.lessons.findIndex(l => l.id === lessonId);
  const progress = ((currentIndex + 1) / course.lessons.length) * 100;

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/courses')}
          sx={{ cursor: 'pointer' }}
        >
          Cursos
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={handleBackToCourse}
          sx={{ cursor: 'pointer' }}
        >
          {course.title}
        </Link>
        <Typography color="text.primary">{currentLesson.title}</Typography>
      </Breadcrumbs>

      {/* Progress bar */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Lección {currentIndex + 1} de {course.lessons.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Math.round(progress)}% completado
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
      </Box>

      {/* Lesson header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" gutterBottom>
                {currentLesson.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip 
                  label={currentLesson.contentType}
                  color="primary"
                  size="small"
                />
                <Chip
                  label={`${currentLesson.duration} min`}
                  variant="outlined"
                  size="small"
                />
                {currentLesson.evaluationId && (
                  <Chip
                    label="Con Evaluación"
                    color="success"
                    size="small"
                  />
                )}
              </Box>
            </Box>

          </Box>

          {/* Objectives */}
          {currentLesson.objectives && currentLesson.objectives.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Objetivos de aprendizaje:
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                {currentLesson.objectives.map((objective, index) => (
                  <Typography key={index} component="li" variant="body2" sx={{ mb: 0.5 }}>
                    {objective}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Lesson content */}
      <Box sx={{ mb: 3 }}>
        {renderLessonContent()}
          </Box>

      {/* Navigation buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
          onClick={handleBackToCourse}
        >
          Volver al curso
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
              onClick={handlePreviousLesson}
            disabled={currentIndex === 0}
            startIcon={<NavigateBefore />}
            >
            Anterior
            </Button>
          
          {currentLesson.evaluationId && !evaluationResult?.passed ? (
            <Button
              variant="contained"
              color="warning"
              onClick={handleStartEvaluation}
              startIcon={<Assignment />}
            >
              {lessonCompleted ? 'Completar evaluación pendiente' : 'Tomar Evaluación'}
            </Button>
          ) : currentLesson.evaluationId ? (
            <Typography variant="body2" color="success.main" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
              ✅ Evaluación completada
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
              Esta lección no tiene evaluación asociada
                </Typography>
          )}
          
          {currentIndex === course.lessons.length - 1 ? (() => {
            const hasEvaluation = currentLesson.evaluationId;
            const isEvaluationCompleted = !hasEvaluation || Boolean(evaluationResult?.passed);
            const shouldDisable = Boolean(hasEvaluation && !isEvaluationCompleted);
            
            return (
              <Button
                variant="contained"
                onClick={handleFinishCourse}
                endIcon={<Flag />}
                disabled={shouldDisable}
                sx={{
                  background: shouldDisable
                    ? 'rgba(0, 0, 0, 0.12)' 
                    : 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)',
                  '&:hover': {
                    background: shouldDisable
                      ? 'rgba(0, 0, 0, 0.12)'
                      : 'linear-gradient(45deg, #388e3c 30%, #4caf50 90%)',
                  },
                  '&:disabled': {
                    color: 'rgba(0, 0, 0, 0.26)',
                  }
                }}
              >
                Finalizar
              </Button>
            );
          })() : (
            <Button
              variant="outlined"
              onClick={currentLesson.evaluationId && !evaluationResult?.passed ? handleBlockedNext : handleNextLesson}
              endIcon={<NavigateNext />}
              sx={{
                ...(currentLesson.evaluationId && !evaluationResult?.passed && {
                  color: 'text.disabled',
                  borderColor: 'text.disabled',
                  '&:hover': {
                    borderColor: 'text.disabled',
                    backgroundColor: 'transparent'
                  }
                })
              }}
            >
              Siguiente
            </Button>
          )}
        </Box>
        </Box>

      {/* Evaluation Dialog */}
      <Dialog
        open={showEvaluation}
        onClose={() => {
          console.log('Diálogo cerrado por onClose');
          setShowEvaluation(false);
        }}
        maxWidth="lg"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Assignment sx={{ color: 'primary.main' }} />
            <Typography variant="h6">
              Evaluación - {currentLesson.title}
              </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <LessonEvaluationDialog
            evaluationId={currentLesson?.evaluationId || ''}
            onEvaluationComplete={handleEvaluationComplete}
            onClose={() => {
              console.log('Diálogo cerrado por componente');
              setShowEvaluation(false);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Evaluation Result Dialog */}
      <Dialog
        open={!!evaluationResult}
        onClose={() => setEvaluationResult(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            {evaluationResult?.passed ? (
              <CheckCircle sx={{ color: 'success.main', fontSize: 30 }} />
            ) : (
              <Assignment sx={{ color: 'error.main', fontSize: 30 }} />
            )}
            <Typography variant="h6">
              {evaluationResult?.passed ? '¡Evaluación Aprobada!' : 'Evaluación No Aprobada'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" gutterBottom color={evaluationResult?.passed ? 'success.main' : 'error.main'}>
              {evaluationResult?.score.toFixed(1)}%
            </Typography>
            <Typography variant="body1" gutterBottom>
              Puntuación obtenida: {evaluationResult?.earnedPoints} de {evaluationResult?.totalPoints} puntos
            </Typography>
            
            {evaluationResult?.passed ? (
              <Alert severity="success" sx={{ mt: 2 }}>
                ¡Felicitaciones! Has aprobado la evaluación. La lección ha sido marcada como completada. Puedes continuar a la siguiente lección o permanecer en esta página.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mt: 2 }}>
                No has alcanzado la puntuación mínima requerida. Puedes intentar la evaluación nuevamente.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {evaluationResult?.passed ? (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={async () => {
                  if (isNavigating) return; // Evitar múltiples clics
                  
                  const isLastLesson = currentIndex === course.lessons.length - 1;
                  console.log(`🚀 Botón "${isLastLesson ? 'Finalizar' : 'Continuar a Siguiente Lección'}" clickeado`);
                  setIsNavigating(true);
                  setEvaluationResult(null);
                  
                  try {
                    console.log('📝 Completando lección...');
                    const success = await handleCompleteLesson();
                    if (success) {
                      console.log('✅ Lección completada, navegando en 500ms...');
                      // Pequeño delay para asegurar que el backend procese la actualización
                      setTimeout(() => {
                        if (isLastLesson) {
                          console.log('🏁 Finalizando curso...');
                          handleFinishCourse();
                        } else {
                          console.log('🚀 Ejecutando handleNextLesson...');
                          handleNextLesson();
                        }
                      }, 500);
                    } else {
                      console.log('❌ Error completando lección, no navegando');
                    }
                  } catch (error) {
                    console.error('Error completando lección:', error);
                    setError('Error completando la lección. Intente de nuevo.');
                  } finally {
                    setIsNavigating(false);
                  }
                }}
                                disabled={isNavigating}
              >
                {isNavigating ? 'Procesando...' : (currentIndex === course.lessons.length - 1 ? 'Finalizar' : 'Continuar a Siguiente Lección')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setEvaluationResult(null)}
              >
                Permanecer Aquí
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                color="warning"
                onClick={() => {
                  setEvaluationResult(null);
                  setShowEvaluation(true);
                }}
              >
                Intentar de Nuevo
              </Button>
              <Button
                variant="outlined"
                onClick={() => setEvaluationResult(null)}
              >
                Cerrar
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Blocked Navigation Dialog */}
      <Dialog
        open={showBlockedMessage}
        onClose={() => setShowBlockedMessage(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Assignment sx={{ color: 'warning.main', fontSize: 30 }} />
            <Typography variant="h6">
              Evaluación Requerida
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body1" gutterBottom>
              Debes aprobar la evaluación de esta lección antes de poder continuar con la siguiente.
            </Typography>
            <Alert severity="info" sx={{ mt: 2 }}>
              Completa la evaluación con la puntuación mínima requerida para desbloquear la siguiente lección.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setShowBlockedMessage(false);
              setShowEvaluation(true);
            }}
          >
            Tomar Evaluación
          </Button>
          <Button
            variant="outlined"
            onClick={() => setShowBlockedMessage(false)}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de curso completado */}
      <Dialog
        open={showCourseCompleted}
        onClose={() => setShowCourseCompleted(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h5" sx={{ color: 'success.main', fontWeight: 'bold' }}>
              🎉 ¡Felicitaciones!
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" gutterBottom>
              Has completado exitosamente el curso
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
              "{course?.title}"
            </Typography>
            
            <Box sx={{ 
              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
              borderRadius: 2,
              p: 3,
              mb: 3,
              color: 'white'
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                🏆 ¡Excelente trabajo!
              </Typography>
              <Typography variant="body1">
                Has completado todas las lecciones del curso. Ahora puedes generar tu certificado de completado.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setShowCourseCompleted(false)}
            sx={{ mr: 2 }}
          >
            Cerrar
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowCourseCompleted(false);
              navigate(`/courses/${courseId}`);
            }}
            sx={{
              background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #388e3c 30%, #4caf50 90%)',
              },
            }}
          >
            Ver Certificado
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LessonView; 